import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  IconButton, MenuItem, TextField, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import ImageUploader from "./ImageUploader";

/* ═══════════════════════════════════════════════════════════════════════════
   TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */
const T = {
  navy: "#0d1b2a", gold: "#c9a96e", cream: "#faf8f4", white: "#fff",
  bg: "#f7f5f1", slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  greenBg: "#edf7ee", greenTx: "#2d6a35", greenBd: "#a8d5ac",
  redTx: "#d32f2f", peach: "#ffebd3", inputBg: "#f8f7f5",
  redBg: "#fef2f2", redBd: "#fecaca",
};

const inputSx = {
  "& .MuiInputBase-root": {
    fontSize: 13.5, borderRadius: "10px", bgcolor: T.inputBg,
    border: `1px solid ${T.line}`, "&.Mui-focused": { bgcolor: T.peach, borderColor: T.gold },
    transition: "all 0.15s", "& fieldset": { border: "none" },
  },
  "& .MuiInputLabel-root": { fontSize: 13 }, "& input": { py: "9px" },
};

const fmtM = (v?: number | string) => {
  const n = Number(v ?? 0);
  return n ? "$" + n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";
};

const PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='140'%3E%3Crect width='200' height='140' fill='%23f0ebe6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca8b7' font-size='13'%3ENo Image%3C/text%3E%3C/svg%3E";

/* ═══════════════════════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════════════════════ */
type PricingType = "pp" | "flat" | "included";

export interface EditServiceItem {
  id: number; name: string; serviceType: string;
  pricingType?: PricingType; price?: number; base_price?: number | null;
  image?: string | null; category?: string; durationHours?: number; tier?: string;
}

interface EditForm {
  name: string; price: string; base_price: string; image: string;
  pricingType: PricingType; category: string; durationHours: string;
}

export interface ServiceImageItem {
  id: number; service_id: number; image_url: string;
  title?: string | null; tag?: string | null;
  is_cover: boolean; sort_order: number;
}

interface Props {
  open: boolean; onClose: () => void;
  item: EditServiceItem | null;
  onSave: (data: { id: number; name: string; price: number; base_price?: number; image?: string; category?: string; pricingType: string; durationHours?: number }) => Promise<void>;
  isSaving: boolean; categoryOptions: string[]; serviceType: string;
  serviceImages?: ServiceImageItem[];
  onUploadImage?: (file: File) => Promise<void>;
  onDeleteImage?: (imgId: number) => Promise<void>;
  onSetCover?: (img: ServiceImageItem) => Promise<void>;
  onReorderImage?: (fromIdx: number, toIdx: number) => Promise<void>;
  onUpdateImageTitle?: (img: ServiceImageItem, title: string) => Promise<void>;
  onUpdateImageTag?: (img: ServiceImageItem, tag: string) => Promise<void>;
  isUploadingImage?: boolean;
}

const PT: { value: PricingType; label: string }[] = [
  { value: "pp", label: "Per Person" }, { value: "flat", label: "Flat Fee" }, { value: "included", label: "Included" },
];
const TAG_OPTIONS = ["Popular", "Luxury", "Premium", "Standard", "Recommended"];
const TAG_COLORS: Record<string, string> = { Popular: "#a16207", Luxury: "#6a1b9a", Premium: "#1565c0", Standard: "#2d6a35", Recommended: "#e65100" };

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
const EditServiceModal: React.FC<Props> = ({
  open, onClose, item, onSave, isSaving, categoryOptions, serviceType,
  serviceImages = [], onUploadImage, onDeleteImage, onSetCover,
  onReorderImage, onUpdateImageTitle, onUpdateImageTag, isUploadingImage,
}) => {
  const [form, setForm] = useState<EditForm>({ name: "", price: "0", base_price: "", image: "", pricingType: "pp", category: "", durationHours: "5" });
  const nameRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [editingTitleId, setEditingTitleId] = useState<number | null>(null);
  const [editTitleVal, setEditTitleVal] = useState("");
  const [imgHover, setImgHover] = useState(false);

  useEffect(() => {
    if (item && open) {
      setForm({ name: item.name, price: String(item.price ?? 0), base_price: String(item.base_price ?? ""), image: item.image ?? "", pricingType: item.pricingType ?? "pp", category: item.category ?? item.tier ?? "", durationHours: String(item.durationHours ?? 5) });
      setTimeout(() => nameRef.current?.focus(), 250);
    }
  }, [item, open]);

  // Sync cover image from gallery to Live Preview when serviceImages change
  useEffect(() => {
    if (!open || serviceImages.length === 0) return;
    const cover = serviceImages.find(i => i.is_cover);
    if (cover) {
      setForm(p => {
        // Only update if current image is empty or is a server URL (not a local data URL being edited)
        if (!p.image || !p.image.startsWith("data:")) {
          return { ...p, image: cover.image_url };
        }
        return p;
      });
    } else if (!form.image && serviceImages.length > 0) {
      setForm(p => ({ ...p, image: serviceImages[0].image_url }));
    }
  }, [serviceImages, open]);

  const setField = (f: keyof EditForm, v: string) => setForm(p => ({ ...p, [f]: v }));

  const priceDiffers = useMemo(() => form.base_price && Number(form.base_price) > 0 && Number(form.price) !== Number(form.base_price), [form.price, form.base_price]);
  const canSave = form.name.trim().length > 0;

  const handleSave = async () => {
    if (!item || !canSave) return;
    await onSave({ id: item.id, name: form.name.trim(), price: Number(form.price) || 0, base_price: form.base_price ? Number(form.base_price) : undefined, image: form.image || undefined, category: form.category || undefined, pricingType: form.pricingType, durationHours: Number(form.durationHours) || undefined });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.ctrlKey && e.key === "Enter" && canSave && !isSaving) handleSave(); };

  if (!item) return null;

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth={false} onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          width: { xs: "96vw", sm: "92vw", md: 880 }, maxWidth: 880,
          borderRadius: "16px", overflow: "hidden",
          boxShadow: "0 12px 56px rgba(13,27,42,0.22), 0 2px 16px rgba(13,27,42,0.08)",
          animation: "editIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
          "@keyframes editIn": { from: { opacity: 0, transform: "scale(0.95) translateY(12px)" }, to: { opacity: 1, transform: "scale(1) translateY(0)" } },
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(13,27,42,0.5)", backdropFilter: "blur(8px)" } } }}
    >
      {/* ═══════════ HEADER ═══════════ */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: 3, py: 1.8,
        background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`,
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", inset: 0, opacity: 0.04, backgroundImage: "radial-gradient(circle at 30% 50%, #fff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, position: "relative", zIndex: 1 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: `${T.gold}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <EditOutlinedIcon sx={{ fontSize: 15, color: T.gold }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white, lineHeight: 1.2 }}>Edit Service Item</Typography>
            <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.45)" }}>{serviceType} catalogue</Typography>
          </Box>
          <Chip label={`#${item.id}`} size="small" sx={{ height: 20, fontSize: 9.5, fontWeight: 600, bgcolor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)", ml: 0.5 }} />
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.5)", position: "relative", zIndex: 1, "&:hover": { color: T.white, bgcolor: "rgba(255,255,255,0.08)" } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ═══════════ BODY — LEFT FORM + RIGHT PREVIEW ═══════════ */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, maxHeight: { md: "75vh" }, overflow: "hidden" }}>

        {/* ──────── LEFT: Form (60%) ──────── */}
        <Box sx={{ flex: "1 1 60%", minWidth: 0, overflowY: "auto", p: { xs: 2.5, sm: 3 }, bgcolor: T.cream }}>

          {/* ── HERO IMAGE CARD ── */}
          <Box
            onMouseEnter={() => setImgHover(true)}
            onMouseLeave={() => setImgHover(false)}
            sx={{
              position: "relative", borderRadius: "14px", overflow: "hidden",
              height: { xs: 180, sm: 220 }, mb: 2.5,
              bgcolor: "#f0ebe6", cursor: "pointer",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              transition: "box-shadow 0.25s",
              "&:hover": { boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
            }}
            onClick={() => coverInputRef.current?.click()}
          >
            {form.image ? (
              <>
                <Box
                  component="img" src={form.image} onError={(e: any) => { e.target.src = PH; }}
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.4s ease", transform: imgHover ? "scale(1.03)" : "scale(1)" }}
                />
                {/* Hover overlay */}
                <Box sx={{
                  position: "absolute", inset: 0,
                  bgcolor: "rgba(13,27,42,0.45)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  opacity: imgHover ? 1 : 0,
                  transition: "opacity 0.25s ease",
                }}>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 0.8,
                    bgcolor: "rgba(255,255,255,0.92)", borderRadius: "24px",
                    px: 2.5, py: 1, backdropFilter: "blur(8px)",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                  }}>
                    <CameraAltOutlinedIcon sx={{ fontSize: 16, color: T.navy }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>Change Image</Typography>
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, background: `linear-gradient(135deg, #f5f0e8 0%, #ebe4d8 100%)` }}>
                <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: T.white, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                  <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 24, color: T.gold }} />
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>Upload cover image</Typography>
                <Typography sx={{ fontSize: 10, color: T.slateLt }}>Click or drag & drop</Typography>
              </Box>
            )}
            <input ref={coverInputRef} type="file" accept="image/*" hidden onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = (ev) => { if (ev.target?.result) setField("image", ev.target.result as string); };
                reader.readAsDataURL(file);
              }
              if (coverInputRef.current) coverInputRef.current.value = "";
            }} />
          </Box>

          {/* ── GALLERY HORIZONTAL STRIP ── */}
          {onUploadImage && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  <CollectionsOutlinedIcon sx={{ fontSize: 13, color: T.slateLt }} />
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Gallery ({serviceImages.length})
                  </Typography>
                </Box>
              </Box>
              <Box sx={{
                display: "flex", gap: 1, overflowX: "auto", pb: 0.8,
                "&::-webkit-scrollbar": { height: 4 },
                "&::-webkit-scrollbar-thumb": { bgcolor: T.line, borderRadius: 2 },
              }}>
                {/* Add image button */}
                <Box
                  onClick={() => galleryInputRef.current?.click()}
                  sx={{
                    width: 80, height: 80, borderRadius: "12px", flexShrink: 0,
                    border: `2px dashed ${T.line}`, bgcolor: T.bg,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    gap: 0.3, cursor: "pointer", transition: "all 0.15s",
                    "&:hover": { borderColor: T.gold, bgcolor: `${T.peach}40` },
                  }}
                >
                  {isUploadingImage ? (
                    <CircularProgress size={18} sx={{ color: T.gold }} />
                  ) : (
                    <>
                      <CloudUploadOutlinedIcon sx={{ fontSize: 18, color: T.gold, opacity: 0.7 }} />
                      <Typography sx={{ fontSize: 8, color: T.slateLt, fontWeight: 500, textAlign: "center", lineHeight: 1.1 }}>Add Image</Typography>
                    </>
                  )}
                </Box>

                {/* Gallery thumbnails */}
                {serviceImages.map((img, idx) => (
                  <Box
                    key={img.id}
                    sx={{
                      width: 80, height: 80, borderRadius: "12px", flexShrink: 0,
                      position: "relative", overflow: "hidden",
                      border: img.is_cover ? `2.5px solid ${T.gold}` : `1.5px solid ${T.line}`,
                      transition: "all 0.2s",
                      "&:hover": { borderColor: T.gold, "& .thumb-actions": { opacity: 1 } },
                    }}
                  >
                    <Box component="img" src={img.image_url} onError={(e: any) => { e.target.src = PH; }}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    {/* Cover badge */}
                    {img.is_cover && (
                      <Box sx={{ position: "absolute", top: 3, left: 3 }}>
                        <StarRoundedIcon sx={{ fontSize: 14, color: T.gold, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }} />
                      </Box>
                    )}
                    {/* Tag */}
                    {img.tag && (
                      <Box sx={{ position: "absolute", bottom: 3, left: 3, bgcolor: "rgba(0,0,0,0.55)", borderRadius: "4px", px: 0.4, py: 0.1 }}>
                        <Typography sx={{ fontSize: 7, color: "#fff", fontWeight: 600 }}>{img.tag}</Typography>
                      </Box>
                    )}
                    {/* Hover actions */}
                    <Box className="thumb-actions" sx={{
                      position: "absolute", inset: 0, bgcolor: "rgba(13,27,42,0.5)",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 0.3,
                      opacity: 0, transition: "opacity 0.2s",
                    }}>
                      {!img.is_cover && (
                        <Tooltip title="Set as cover">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onSetCover?.(img); }} sx={{ bgcolor: "rgba(255,255,255,0.85)", width: 24, height: 24, "&:hover": { bgcolor: T.white } }}>
                            <StarBorderRoundedIcon sx={{ fontSize: 13, color: T.gold }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDeleteImage?.(img.id); }} sx={{ bgcolor: "rgba(255,255,255,0.85)", width: 24, height: 24, "&:hover": { bgcolor: "#fee2e2" } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 13, color: T.redTx }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
              <input ref={galleryInputRef} type="file" accept="image/*" hidden onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) await onUploadImage(file);
                if (galleryInputRef.current) galleryInputRef.current.value = "";
              }} />
            </Box>
          )}

          {/* ── CARD 1: Basic Info ── */}
          <Box sx={{ bgcolor: T.white, borderRadius: "14px", p: 2.5, mb: 2, border: `1px solid ${T.line}`, boxShadow: "0 1px 4px rgba(0,0,0,0.03)" }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 2 }}>
              Basic Info
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField inputRef={nameRef} size="small" fullWidth label="Name" value={form.name}
                onChange={e => setField("name", e.target.value)} error={!form.name.trim()}
                helperText={!form.name.trim() ? "Name is required" : ""} sx={inputSx}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                {categoryOptions.length > 0 && (
                  <TextField select size="small" fullWidth label="Category" value={form.category}
                    onChange={e => setField("category", e.target.value)} sx={inputSx}
                  >
                    {categoryOptions.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                )}
                {serviceType === "package" && (
                  <TextField size="small" fullWidth type="number" label="Duration (hours)"
                    value={form.durationHours} onChange={e => setField("durationHours", e.target.value)}
                    inputProps={{ min: 1 }} sx={inputSx}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* ── CARD 2: Pricing ── */}
          <Box sx={{ bgcolor: T.white, borderRadius: "14px", p: 2.5, border: `1px solid ${priceDiffers ? T.redBd : T.line}`, boxShadow: "0 1px 4px rgba(0,0,0,0.03)", transition: "border-color 0.25s" }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 2 }}>
              Pricing
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField select size="small" fullWidth label="Pricing Type" value={form.pricingType}
                onChange={e => setField("pricingType", e.target.value)} sx={inputSx}
              >
                {PT.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </TextField>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField size="small" fullWidth type="number" label="Base Price ($)" value={form.base_price}
                  onChange={e => setField("base_price", e.target.value)} inputProps={{ min: 0, step: 0.01 }}
                  placeholder="e.g. 20.00" sx={inputSx}
                />
                <TextField size="small" fullWidth type="number" label="Selling Price ($)" value={form.price}
                  onChange={e => setField("price", e.target.value)} inputProps={{ min: 0, step: 0.01 }}
                  placeholder="e.g. 25.00"
                  sx={{ ...inputSx, ...(priceDiffers ? { "& .MuiInputBase-root": { ...inputSx["& .MuiInputBase-root"], border: `2px solid ${T.redTx}`, bgcolor: T.redBg } } : {}) }}
                />
              </Box>
              {priceDiffers && (
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 0.8,
                  px: 1.5, py: 0.8, bgcolor: T.redBg, borderRadius: "10px", border: `1px solid ${T.redBd}`,
                  animation: "warnIn 0.2s ease", "@keyframes warnIn": { from: { opacity: 0, transform: "translateY(-4px)" }, to: { opacity: 1, transform: "translateY(0)" } },
                }}>
                  <WarningAmberRoundedIcon sx={{ fontSize: 15, color: T.redTx }} />
                  <Typography sx={{ fontSize: 11, color: T.redTx, fontWeight: 600 }}>
                    Selling price differs from base price
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* ──────── RIGHT: Live Preview (40%) — sticky ──────── */}
        <Box sx={{
          width: { xs: "100%", md: 320 }, flexShrink: 0,
          background: `linear-gradient(180deg, ${T.bg} 0%, #ede8e0 100%)`,
          borderLeft: { md: `1px solid ${T.line}` },
          borderTop: { xs: `1px solid ${T.line}`, md: "none" },
          display: "flex", flexDirection: "column",
          position: { md: "sticky" }, top: 0,
          overflowY: "auto",
        }}>
          {/* Preview header */}
          <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Live Preview
            </Typography>
          </Box>

          {/* Preview card */}
          <Box sx={{ mx: 2.5, mb: 2.5, borderRadius: "16px", overflow: "hidden", bgcolor: T.white, boxShadow: "0 4px 20px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)", transition: "all 0.25s" }}>

            {/* Large image */}
            <Box sx={{ position: "relative", width: "100%", aspectRatio: "16/10", bgcolor: "#f0ebe6", overflow: "hidden" }}>
              {form.image ? (
                <Box
                  key={form.image}
                  component="img" src={form.image} onError={(e: any) => { e.target.src = PH; }}
                  sx={{
                    width: "100%", height: "100%", objectFit: "cover", display: "block",
                    animation: "previewImgIn 0.35s ease",
                    "@keyframes previewImgIn": { from: { opacity: 0, transform: "scale(1.04)" }, to: { opacity: 1, transform: "scale(1)" } },
                  }}
                />
              ) : (
                <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: T.line, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 18, color: T.slateLt }} />
                  </Box>
                  <Typography sx={{ fontSize: 10, color: T.slateLt }}>No image</Typography>
                </Box>
              )}
              {/* Gradient for text readability */}
              {form.image && (
                <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "50%", background: "linear-gradient(to top, rgba(13,27,42,0.5) 0%, transparent 100%)", pointerEvents: "none" }} />
              )}
            </Box>

            {/* Details */}
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.navy, lineHeight: 1.25, mb: 0.5, minHeight: 20 }}>
                {form.name || "Item Name"}
              </Typography>

              {/* Category + pricing type chips */}
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.5 }}>
                {form.category && (
                  <Chip label={form.category} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, color: T.slate, bgcolor: `${T.slate}10`, border: `1px solid ${T.slate}20` }} />
                )}
                <Chip
                  label={form.pricingType === "pp" ? "Per Person" : form.pricingType === "flat" ? "Flat Fee" : "Included"}
                  size="small"
                  sx={{ height: 20, fontSize: 9, fontWeight: 500, bgcolor: T.greenBg, color: T.greenTx }}
                />
                {serviceType === "package" && form.durationHours && (
                  <Chip label={`${form.durationHours} hrs`} size="small" sx={{ height: 20, fontSize: 9, bgcolor: "#e3f2fd", color: "#1565c0" }} />
                )}
              </Box>

              {/* Price display */}
              <Box sx={{
                bgcolor: priceDiffers ? T.redBg : T.peach,
                borderRadius: "12px", p: 1.8,
                border: `1px solid ${priceDiffers ? T.redBd : `${T.gold}25`}`,
                transition: "all 0.25s",
              }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                  <Box>
                    <Typography sx={{ fontSize: 8, fontWeight: 700, color: priceDiffers ? T.redTx : T.gold, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Selling Price
                    </Typography>
                  </Box>
                  <Typography
                    key={form.price}
                    sx={{
                      fontSize: 26, fontWeight: 800, color: T.navy, lineHeight: 1, fontVariantNumeric: "tabular-nums",
                      animation: "priceFlash 0.3s ease",
                      "@keyframes priceFlash": { "0%": { transform: "scale(0.95)" }, "50%": { transform: "scale(1.03)" }, "100%": { transform: "scale(1)" } },
                    }}
                  >
                    {fmtM(form.price)}
                  </Typography>
                </Box>
                {priceDiffers && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, pt: 1, borderTop: `1px solid ${T.redBd}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <WarningAmberRoundedIcon sx={{ fontSize: 12, color: T.redTx }} />
                      <Typography sx={{ fontSize: 10, color: T.redTx, fontWeight: 500 }}>Base Price</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.redTx }}>{fmtM(form.base_price)}</Typography>
                  </Box>
                )}
                {!priceDiffers && form.base_price && Number(form.base_price) > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, pt: 1, borderTop: `1px solid ${T.gold}20` }}>
                    <Typography sx={{ fontSize: 10, color: T.slateLt, fontWeight: 500 }}>Base Price</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{fmtM(form.base_price)}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Gallery mini-preview in sidebar */}
          {serviceImages.length > 0 && (
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
                Gallery ({serviceImages.length})
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.6 }}>
                {serviceImages.slice(0, 6).map((img, i) => (
                  <Box key={img.id} sx={{ aspectRatio: "1", borderRadius: "8px", overflow: "hidden", position: "relative" }}>
                    <Box component="img" src={img.image_url} onError={(e: any) => { e.target.src = PH; }}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    {i === 5 && serviceImages.length > 6 && (
                      <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography sx={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>+{serviceImages.length - 6}</Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* ═══════════ FOOTER ═══════════ */}
      <DialogActions sx={{
        px: 3, py: 1.8, borderTop: `1px solid ${T.line}`, bgcolor: T.white, gap: 1,
      }}>
        <Typography sx={{ fontSize: 10, color: T.slateLt, mr: "auto" }}>Ctrl+Enter to save</Typography>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 12, fontWeight: 500, color: T.slate, borderRadius: "10px", px: 2.5 }}>
          Cancel
        </Button>
        <Button
          variant="contained" onClick={handleSave} disabled={isSaving || !canSave}
          startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 15 }} />}
          sx={{
            textTransform: "none", fontSize: 12, fontWeight: 600,
            bgcolor: T.navy, borderRadius: "10px", boxShadow: "0 2px 8px rgba(13,27,42,0.15)",
            px: 3, py: 0.8,
            "&:hover": { bgcolor: "#1b3148", boxShadow: "0 4px 14px rgba(13,27,42,0.2)" },
            "&.Mui-disabled": { bgcolor: T.line, color: T.slateLt },
          }}
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditServiceModal;
